import * as React from 'react';
import { memo } from 'react';
import Markdown, { Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';

type MarkdownRendererProps = {
  children: string;
  className?: string;
};

const components: Partial<Components> = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  pre: ({ children }) => <>{children}</>,
};

// The model re-emits its answer after a tool call resolves, so the same
// paragraph arrives twice in a row — "Your manager is Cheery Malakai." was
// rendered as two identical <p> elements (FLA-8200 §2). Consecutive only:
// a repeated line further down the answer is usually deliberate.
export function dropRepeatedParagraphs(text: string): string {
  const paragraphs = text.split(/\n{2,}/);
  const kept: string[] = [];
  for (const paragraph of paragraphs) {
    const previous = kept[kept.length - 1];
    if (previous !== undefined && previous.trim() === paragraph.trim()) {
      continue;
    }
    kept.push(paragraph);
  }
  return kept.join('\n\n');
}

function MarkdownRendererInner({ children, className }: MarkdownRendererProps) {
  return (
    <div className={clsx('ai-chat-markdown', className)}>
      <Markdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={components}>
        {dropRepeatedParagraphs(children)}
      </Markdown>
    </div>
  );
}

export const MarkdownRenderer = memo(
  MarkdownRendererInner,
  (prev, next) => prev.children === next.children,
);
MarkdownRenderer.displayName = 'MarkdownRenderer';
